import { otherColour, type Colour, type RoundEntry, type Score } from '../model/types.js';
import type { Pair, PairingEngine, PairingPlayer, PairingRequest } from '../pairing/types.js';
import { pointSystemFromTrf } from './scoring.js';
import type { TrfDocument } from './types.js';

/** Number of rounds that have any data in the file. */
export function roundsInTrf(doc: TrfDocument): number {
  let n = 0;
  for (const p of doc.players) n = Math.max(n, p.rounds.length);
  return n;
}

function entry(rounds: readonly RoundEntry[], r: number): RoundEntry {
  return rounds[r - 1] ?? { opponent: null, colour: null, result: 'Z' };
}

/**
 * The initial colour: from the file (152 / XXC) or inferred like bbpPairings
 * does: the first round with colours, the highest-ranked participant with a
 * colour there; reversed for every higher-ranked participant without one.
 */
export function inferInitialColour(doc: TrfDocument): Colour {
  if (doc.initialColour !== null) return doc.initialColour;
  const total = roundsInTrf(doc);
  const sorted = [...doc.players].sort((a, b) => a.startRank - b.startRank);
  for (let r = 1; r <= total; r++) {
    if (!sorted.some((p) => entry(p.rounds, r).colour !== null)) continue;
    let k = 0;
    for (const p of sorted) {
      const participated = p.rounds
        .slice(0, r)
        .some((e) => e.opponent !== null || e.result === 'U');
      if (!participated) continue;
      const c = entry(p.rounds, r).colour;
      if (c !== null) return k % 2 === 0 ? c : otherColour(c);
      k++;
    }
  }
  return 'w';
}

function accelerationFor(doc: TrfDocument, id: number, round: number): Score {
  let s = 0;
  for (const a of doc.accelerations) {
    if (id >= a.firstId && id <= a.lastId && round >= a.firstRound && round <= a.lastRound) {
      s += a.points;
    }
  }
  return s;
}

export type TrfInputMode = 'pair' | 'check';

/**
 * Build the engine request for `round` from a TRF document.
 *
 * In 'check' mode the round exists in the file: a player is available when
 * they were paired (with an opponent, including forfeits) or got the PAB.
 * In 'pair' mode the round is the next one: players listed as byes (240) or
 * absent (XXZ) are unavailable.
 */
export function trfPairingRequest(
  doc: TrfDocument,
  round: number,
  mode: TrfInputMode,
): PairingRequest {
  const absent = new Set<number>();
  if (mode === 'pair') {
    for (const b of doc.byes) if (b.round === round) b.ids.forEach((i) => absent.add(i));
    doc.absentNextRound.forEach((i) => absent.add(i));
  }
  const players: PairingPlayer[] = [...doc.players]
    .sort((a, b) => a.startRank - b.startRank)
    .map((p) => {
      const history: RoundEntry[] = [];
      for (let r = 1; r < round; r++) history.push(entry(p.rounds, r));
      let available: boolean;
      if (mode === 'check') {
        const e = entry(p.rounds, round);
        available = e.opponent !== null || e.result === 'U';
      } else {
        available = !absent.has(p.startRank);
      }
      const acceleration = accelerationFor(doc, p.startRank, round);
      const accelerationHistory: Score[] = [];
      for (let r = 1; r < round; r++)
        accelerationHistory.push(accelerationFor(doc, p.startRank, r));
      return {
        id: p.startRank,
        rating: p.rating,
        history,
        available,
        ...(acceleration !== 0 ? { acceleration } : {}),
        ...(accelerationHistory.some((x) => x !== 0) ? { accelerationHistory } : {}),
      };
    });
  const forbiddenPairs: [number, number][] = [];
  for (const f of doc.forbidden) {
    if (round < f.firstRound || round > f.lastRound) continue;
    for (let i = 0; i < f.ids.length; i++) {
      for (let j = i + 1; j < f.ids.length; j++) {
        forbiddenPairs.push([f.ids[i] as number, f.ids[j] as number]);
      }
    }
  }
  return {
    round,
    totalRounds: doc.totalRounds ?? Math.max(round, roundsInTrf(doc)),
    players,
    pointSystem: pointSystemFromTrf(doc.scoring),
    initialColour: inferInitialColour(doc),
    ...(forbiddenPairs.length > 0 ? { forbiddenPairs } : {}),
  };
}

/** The pairing actually recorded in the file for a round. */
export function recordedPairing(
  doc: TrfDocument,
  round: number,
): { pairs: Pair[]; byes: number[] } {
  const pairs: Pair[] = [];
  const byes: number[] = [];
  const seen = new Set<number>();
  for (const p of [...doc.players].sort((a, b) => a.startRank - b.startRank)) {
    const e = entry(p.rounds, round);
    if (e.result === 'U') byes.push(p.startRank);
    if (e.opponent === null || seen.has(p.startRank)) continue;
    seen.add(p.startRank);
    seen.add(e.opponent);
    if (e.colour === 'b') pairs.push({ white: e.opponent, black: p.startRank });
    else pairs.push({ white: p.startRank, black: e.opponent });
  }
  return { pairs, byes };
}

export interface RoundCheck {
  readonly round: number;
  readonly ok: boolean;
  /** Pairs in the file but not produced by the engine ("w-b"). */
  readonly missing: readonly string[];
  /** Pairs produced by the engine but not in the file. */
  readonly extra: readonly string[];
  readonly expectedByes: readonly number[];
  readonly actualByes: readonly number[];
  readonly error?: string;
}

const key = (p: Pair): string => `${p.white}-${p.black}`;

/**
 * Re-pair every round of a tournament with an engine and compare with the
 * recorded pairings (a free pairings checker). Colours are compared too.
 */
export function checkTrf(
  doc: TrfDocument,
  engine: PairingEngine,
  onlyRound?: number,
): RoundCheck[] {
  const out: RoundCheck[] = [];
  const total = roundsInTrf(doc);
  for (let r = 1; r <= total; r++) {
    if (onlyRound !== undefined && r !== onlyRound) continue;
    const rec = recordedPairing(doc, r);
    try {
      const res = engine.pair(trfPairingRequest(doc, r, 'check'));
      const want = new Set(rec.pairs.map(key));
      const got = new Set(res.pairs.map(key));
      const missing = [...want].filter((k) => !got.has(k));
      const extra = [...got].filter((k) => !want.has(k));
      const eb = [...rec.byes].sort((a, b) => a - b);
      const ab = [...res.byes].sort((a, b) => a - b);
      const byesOk = eb.length === ab.length && eb.every((v, i) => v === ab[i]);
      out.push({
        round: r,
        ok: missing.length === 0 && extra.length === 0 && byesOk,
        missing,
        extra,
        expectedByes: eb,
        actualByes: ab,
      });
    } catch (e) {
      out.push({
        round: r,
        ok: false,
        missing: rec.pairs.map(key),
        extra: [],
        expectedByes: rec.byes,
        actualByes: [],
        error: e instanceof Error ? e.message : String(e),
      });
    }
  }
  return out;
}
